import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
} from '@mui/material';
import './manualOnboarding.css';
import { JSONContainer } from '../../../../../components/AccountOnboarding/ManualAccountOnboarding/JSONContainer';
import CustomButton from '../../../../../components/MasterComponent/Button/CustomButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useCopyText } from '../../../../../hooks/useCopyText';
import { S3_CREATION_STEPS } from '../config';

interface CreateS3BucketProps {
  onComplete: () => void;
  onBack: () => void;
  onAccountNumberUpdate: (accountNumber: string) => void;
}

const CreateS3Bucket: React.FC<CreateS3BucketProps> = ({
  onComplete,
  onBack,
  onAccountNumberUpdate,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [accountNumber, setAccountNumber] = useState('');
  const [bucketName, setBucketName] = useState('');
  const [isValidAccountNumber, setIsValidAccountNumber] = useState(false);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const { isCopied, copyToClipboard } = useCopyText();

  // Generate bucket name when account number changes
  useEffect(() => {
    const validAccount = /^\d{12}$/.test(accountNumber);
    setIsValidAccountNumber(validAccount);

    if (validAccount) {
      const newBucketName = `cfx-cost-usage-${accountNumber}-us-east-1`;
      setBucketName(newBucketName);
      onAccountNumberUpdate(accountNumber);
    } else {
      setBucketName('');
    }
  }, [accountNumber, onAccountNumberUpdate]);

  const handleCopyBucketName = (index: number) => {
    if (index === 1 && bucketName) {
      navigator.clipboard.writeText(bucketName).then(
        () => {
          // Update completion status for this step
          const newCompleted = { ...completed };
          newCompleted[index] = true;
          setCompleted(newCompleted);

          // Wait for 3 seconds then reset
          setTimeout(() => {
            // This happens after button component handles the success state
          }, 3000);
        },
        (err) => {
          console.error('Could not copy text: ', err);
        }
      );
    }
  };

  const handleNext = () => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const totalSteps = () => {
    return S3_CREATION_STEPS(bucketName).length;
  };

  const allStepsCompleted = totalSteps() - 1 === activeStep;

  const handleAccountNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setAccountNumber(value);
  };

  return (
    <Box className="createS3BucketContainer">
      <Paper elevation={0} className="instructionsPaper">
        <p className="instructions">
          Follow these steps to create an S3 bucket in your AWS console. This
          bucket will store your AWS Cost and Usage Reports (CUR), which
          CloudForestX needs to analyze your cloud spending.
        </p>
      </Paper>

      <Box className="accountNumberInputSection">
        <Typography variant="subtitle1" className="accountNumberLabel">
          Enter your 12-digit AWS Account Number:
        </Typography>
        <TextField
          value={accountNumber}
          onChange={handleAccountNumberChange}
          error={accountNumber !== '' && !isValidAccountNumber}
          helperText={
            accountNumber !== '' && !isValidAccountNumber
              ? 'Please enter a valid 12-digit AWS account number'
              : ''
          }
          placeholder="e.g. 123456789012"
          variant="outlined"
          size="small"
          className="accountNumberInput"
          inputProps={{ maxLength: 12 }}
        />
      </Box>

      {isValidAccountNumber && (
        <Box className="s3BucketNameDisplay">
          <Typography variant="subtitle1" fontWeight={500}>
            Your S3 bucket name will be:
          </Typography>
          <Paper elevation={0} className="bucketNamePaper">
            <div className="bucketNameText d-flex align-center">
              {bucketName}{' '}
              <IconButton
                size="small"
                onClick={() => copyToClipboard(bucketName)}
                className="copyNameButton"
              >
                {isCopied ? (
                  <CheckCircleOutlineIcon
                    style={{ color: '#3f51b5' }}
                    fontSize="small"
                  />
                ) : (
                  <ContentCopyIcon
                    style={{ color: '#3f51b5' }}
                    fontSize="small"
                  />
                )}
              </IconButton>
            </div>
          </Paper>
          <Typography variant="body2" color="textSecondary">
            Copy this exact name when creating your S3 bucket
          </Typography>
        </Box>
      )}

      {isValidAccountNumber && (
        <Box className="s3StepsContainer">
          <p className="bucketStepTitle">S3 Bucket Creation Steps</p>
          <Stepper
            activeStep={activeStep}
            orientation="vertical"
            className="s3Stepper"
          >
            {S3_CREATION_STEPS(bucketName).map((step, index) => (
              <Step key={index} completed={completed[index]}>
                <StepLabel>
                  <h6>{step.name}</h6>
                </StepLabel>
                <StepContent>
                  <JSONContainer
                    key={index}
                    index={index}
                    policy={step}
                    activeStep={activeStep}
                    totalSteps={totalSteps()}
                    onCopyJson={handleCopyBucketName}
                    onNext={handleNext}
                    onBack={handleBack}
                    stepType="Step"
                    suggestedNameType="bucket"
                  />
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      <Box className="navigationButtons">
        <CustomButton buttonType="outlined" onClickHandler={onBack}>
          Back
        </CustomButton>
        <div className="d-flex align-center">
          {!allStepsCompleted && (
            <p className="warningMessageManualOnboarding mr-10">
              ( Please complete all steps along with the exact S3 bucket naming
              pattern shown before proceeding. )
            </p>
          )}
          <CustomButton
            buttonType="contained"
            disabled={!allStepsCompleted}
            onClickHandler={onComplete}
          >
            Next: Create Cost & Usage Report
          </CustomButton>
        </div>
      </Box>
    </Box>
  );
};

export default CreateS3Bucket;
